import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { EmailLengthText, GenerateEmailQuery, Reaction } from '../../../../domain/generatedEmail';

import { FeedbackButton } from './FeedbackButton';
import { RegenerateButton } from './RegenerateButton';
import {
  StyledCopyButton,
  StyledEmailPreviewFooter,
  StyledFeedbackButtons,
  StyledVariantsNavigationButton,
  StyledVariantsView,
} from './styles';

export type EmailPreviewFooterProps = {
  queries: GenerateEmailQuery[];
  emailIndex: number;
  isStale: boolean;
  onEmailIndexChange(index: number): void;
  onRegenerate(lengthText: EmailLengthText): void;
  onFeedback(reaction: Reaction): void;
  onCopy(): void;
};

export const EmailPreviewFooter = (props: EmailPreviewFooterProps) => {
  const { emailIndex, onEmailIndexChange, queries, onRegenerate, onFeedback, onCopy, isStale } = props;
  const { t } = useTranslation();

  const { isLoading, requestId } = queries[emailIndex] || {};
  const queriesCount = queries.length;

  return (
    <StyledEmailPreviewFooter data-automation="EmailPreviewFooter">
      {queriesCount > 1 ? (
        <>
          <StyledVariantsNavigationButton
            disabled={emailIndex <= 0}
            onClick={() => onEmailIndexChange(emailIndex - 1)}
            data-automation="VariantsNavButton"
          >
            <SWReactIcons iconName="chev-left" size="xs" />
          </StyledVariantsNavigationButton>
          <StyledVariantsView data-automation="EmailVariantsView">
            {emailIndex + 1}/{queriesCount}
          </StyledVariantsView>
          <StyledVariantsNavigationButton
            disabled={emailIndex >= queriesCount - 1}
            onClick={() => onEmailIndexChange(emailIndex + 1)}
            data-automation="VariantsNavButton"
          >
            <SWReactIcons iconName="chev-right" size="xs" />
          </StyledVariantsNavigationButton>
        </>
      ) : null}
      <RegenerateButton isStale={isStale} queries={queries} onRegenerate={onRegenerate} emailIndex={emailIndex} />
      <StyledFeedbackButtons data-automation="FeedbackButtons">
        <FeedbackButton
          isLoading={isLoading}
          requestId={requestId}
          reaction="like"
          onFeedback={() => onFeedback('like')}
          data-automation="FeedbackButtonLike"
        />

        <FeedbackButton
          isLoading={isLoading}
          requestId={requestId}
          reaction="dislike"
          onFeedback={() => onFeedback('dislike')}
          data-automation="FeedbackButtonDisike"
        />
      </StyledFeedbackButtons>

      <StyledCopyButton variant="flat" disabled={isLoading} onClick={onCopy} data-automation="CopyButton">
        <SWReactIcons
          iconName="copy"
          size="12px"
          iconColor={isLoading ? colorsPalettes.carbon[100] : colorsPalettes.blue[400]}
        />
        {t('common.copy')}
      </StyledCopyButton>
    </StyledEmailPreviewFooter>
  );
};
